<div class="w-full max-w-full">
  <div class="flex items-center justify-between p-4">
    <div class="text-lg font-bold">
      {{ 'Related Logs for' | transloco }} {{ data.logType }} - {{ data.parentRecordNo }}
    </div>
    <button mat-icon-button (click)="close()" aria-label="Close">
      <mat-icon svgIcon="heroicons_outline:x"></mat-icon>
    </button>
  </div>

  <div class="px-4 pb-4">
    <div class="max-h-[60vh] min-h-[50vh] overflow-auto border rounded relative">
      <!-- Loading Overlay -->
      <div
        *ngIf="isLoading"
        class="absolute inset-0 flex items-center justify-center bg-card bg-opacity-90 z-10"
      >
        <mat-spinner></mat-spinner>
      </div>
      <table mat-table [dataSource]="logs" *ngIf="!isLoading && logs.length > 0">
        <!-- ID Column -->
        <ng-container matColumnDef="unique_id">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2 text-center"
          >
            {{ 'ID' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2 text-center">
            <span class="rounded-lg px-3 whitespace-nowrap">
              {{ log.unique_id }}
            </span>
          </td>
        </ng-container>

        <!-- Parent MIR/WIR Log Column -->
        <ng-container matColumnDef="parent_record">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ data.logType }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            {{ data.parentRecordNo ? (data.parentRecordNo | uppercase) : '-' }}
          </td>
        </ng-container>

        <!-- Record No. Column -->
        <ng-container matColumnDef="unifier_observation_id">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'record_no' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            {{ log.unifier_observation_id ? (log.unifier_observation_id | uppercase) : '-' }}
          </td>
        </ng-container>

        <!-- Sequence No. Column -->
        <ng-container matColumnDef="unifier_sequence_number">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'sequence_no' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            <span [matTooltip]="log.unifier_sequence_number">
              {{ log.unifier_sequence_number ? (log.unifier_sequence_number | uppercase) : '-' }}
            </span>
          </td>
        </ng-container>

        <!-- Rev# Column -->
        <ng-container matColumnDef="unifier_total_record_revisions">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'Rev#' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            {{ log.unifier_total_record_revisions ?? '-' }}
          </td>
        </ng-container>

        <!-- Title Column -->
        <ng-container matColumnDef="title">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'title' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            <span
              [matTooltip]="log.title && log.title.length > 30 ? log.title : ''"
              class="text-ellipsis overflow-hidden"
              style="max-width: 200px; display: inline-block"
            >
              {{ log.title }}
            </span>
          </td>
        </ng-container>

        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'Status' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            <span
              *ngIf="log.type !== 'note'"
              [matTooltip]="log.status"
              [ngClass]="getStatusClass(log.is_draft, log.status)"
              class="rounded-lg px-3 py-0.5 inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {{ (log.is_draft ? 'draft' : log.status).replace('_', ' ') | transloco }}
            </span>
            <span *ngIf="log.type === 'note'">
              <mat-icon svgIcon="heroicons_solid:minus-sm"></mat-icon>
            </span>
          </td>
        </ng-container>

        <!-- Creator Column -->
        <ng-container matColumnDef="creator">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'Creator' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            <span
              [matTooltip]="log.creator?.name + ' (' + log.creator?.status + ')'"
              [ngClass]="{
                'bg-gray-500': log.creator?.status === 'Inactive',
                'bg-green-500': log.creator?.status === 'Active',
                'bg-orange-500': log.creator?.status === 'Deactivated',
                'bg-red-500': log.creator?.status === 'Expired'
              }"
              class="circle-creator cursor-default"
            >
              {{ log.creator?.name?.substring(0, 2).toUpperCase() }}
            </span>
          </td>
        </ng-container>

        <!-- Created At Column -->
        <ng-container matColumnDef="created_at">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'Created At' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            <div class="flex flex-col leading-tight">
              <span>{{ log.created_at | date: 'yyyy-MM-dd' }}</span>
              <span class="text-xs text-secondary">{{ log.created_at | date: 'hh:mm a' }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Updated At Column -->
        <ng-container matColumnDef="updated_at">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'Updated At' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            <div class="flex flex-col leading-tight">
              <span>{{ log.updated_at | date: 'yyyy-MM-dd' }}</span>
              <span class="text-xs text-secondary">{{ log.updated_at | date: 'hh:mm a' }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Cluster Name Column -->
        <ng-container matColumnDef="cluster_name">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'Cluster Name' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            <span
              [matTooltip]="
                getClusterName(log) && getClusterName(log).length > 25 ? getClusterName(log) : ''
              "
              class="text-ellipsis overflow-hidden"
              style="max-width: 200px; display: inline-block"
            >
              {{ getClusterName(log) }}
            </span>
          </td>
        </ng-container>

        <!-- Project Number Column -->
        <ng-container matColumnDef="project_number">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            <div
              class="flex justify-center leading-tight"
              [ngClass]="isRtl ? 'flex-col-reverse' : 'flex-col'"
            >
              <span>{{ 'Project' | transloco }}</span>
              <span>{{ 'Number' | transloco }}</span>
            </div>
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            <span
              class="text-ellipsis overflow-hidden"
              style="max-width: 200px; display: inline-block"
            >
              {{ log.project_number || log.projects?.project_number || '-' }}
            </span>
          </td>
        </ng-container>

        <!-- Action Column -->
        <ng-container matColumnDef="action">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="text-white text-xs uppercase tracking-wide p-2"
          >
            {{ 'Action' | transloco }}
          </th>
          <td mat-cell *matCellDef="let log" class="text-sm p-2">
            <button
              mat-icon-button
              (click)="openLogDetails(log)"
              [matTooltip]="'Open Log' | transloco"
              color="primary"
            >
              <mat-icon svgIcon="open_in_new"></mat-icon>
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns" class="bg-card"></tr>
        <tr mat-row *matRowDef="let log; columns: displayedColumns"></tr>
      </table>

      <div
        *ngIf="!isLoading && logs.length === 0"
        class="w-full h-40 flex items-center justify-center text-secondary"
      >
        {{ 'No related logs found' | transloco }}
      </div>
    </div>

    <!-- Pagination -->
    <mat-paginator
      *ngIf="meta && meta.total_items > 0"
      [length]="meta.total_items"
      [pageSize]="pageSize"
      [pageSizeOptions]="[20, 40, 60]"
      [pageIndex]="pageIndex"
      (page)="onPageChange($event.pageIndex); pageSize = $event.pageSize"
      showFirstLastButtons
    >
    </mat-paginator>
  </div>
</div>
